import { ContactResponse } from "pages/media/types/";
import { BooleanOption } from "../types";
import { ThemeResponse } from "pages/media/components/themeEditor/hooks/useThemeApi/";
import { useTranslate } from "@refinedev/core";
import { SelectProps } from "antd";

export const transitionsToSelectValue = (
  val: string | null | undefined
): BooleanOption => {
  if (val === "Basic") return "enabled";
  if (val === "NoTransition") return "disabled";
  return "auto";
};

export const selectValueToTransitions = (
  val: BooleanOption | null | undefined
): "Basic" | "NoTransition" | null => {
  if (val === "enabled") return "Basic";
  if (val === "disabled") return "NoTransition";
  return null;
};

// Maps a tri-state boolean (true / false / null) to the Enabled / Disabled /
// Auto select value, so a nullable boolean can be cleared back to "auto"
// (null) instead of being forced to false.
export const booleanToSelectValue = (
  val: boolean | null | undefined
): BooleanOption => {
  if (val === true) return "enabled";
  if (val === false) return "disabled";
  return "auto";
};

export const selectValueToBoolean = (
  val: BooleanOption | null | undefined
): boolean | null => {
  if (val === "enabled") return true;
  if (val === "disabled") return false;
  return null;
};

export const buildBooleanFieldValues = (
  fields: readonly string[],
  settings: Record<string, unknown> | null | undefined
) =>
  fields.reduce(
    (acc, field) => {
      // These fields default to true, so treat a null/undefined value as enabled.
      acc[field] = (settings?.[field] as boolean | null | undefined) ?? true;
      return acc;
    },
    {} as Record<string, boolean>
  );

export const buildBooleanFieldValuesForSave = (
  fields: readonly string[],
  formValues: Record<string, boolean | undefined>
) =>
  fields.reduce(
    (acc, field) => {
      acc[field] = formValues[field] ?? null;
      return acc;
    },
    {} as Record<string, boolean | null>
  );

export const mapToOptions = <T extends { id: string; name: string }>(
  items: T[]
): SelectProps["options"] =>
  items.map((item) => ({ label: item.name, value: item.id }));

export const mapContactToOptions = (
  contacts: ContactResponse[]
): SelectProps["options"] =>
  contacts.map((contact) => ({
    label: `${contact.firstname} ${contact.lastname}`,
    value: contact.id,
  }));

export const mapToThemeOptions = (
  publicThemes: ThemeResponse[],
  orgThemes: ThemeResponse[],
  t: ReturnType<typeof useTranslate>,
  aspectRatioFilter?: string
): SelectProps["options"] => {
  const filterByAspectRatio = (themes: ThemeResponse[]) =>
    aspectRatioFilter
      ? themes.filter((theme) => theme.aspect_ratio === aspectRatioFilter)
      : themes;

  const filteredPublic = filterByAspectRatio(publicThemes);
  const filteredOrg = filterByAspectRatio(orgThemes);

  const options: SelectProps["options"] = [];

  if (filteredPublic.length > 0) {
    options.push({
      label: t("media.organizations.index.publicThemes"),
      options: mapToOptions(filteredPublic),
    });
  }

  if (filteredOrg.length > 0) {
    options.push({
      label: t("media.organizations.index.organizationThemes"),
      options: mapToOptions(filteredOrg),
    });
  }

  return options;
};
